// Core storefront vocabulary for the typo fallback. Checked before any API
// round-trips. Words the catalog only uses inside compounds ("toothpaste",
// "Pre-Pasted") are listed as stems too ("paste"), since the search backend
// matches them inside longer words.
const PREDICTIVE_SEARCH_VOCABULARY = [
  'paste', 'toothpaste', 'toothbrush', 'toothbrushes', 'brush', 'teeth', 'tooth',
  'mouthwash', 'mouth', 'wash', 'rinse', 'floss', 'flosser', 'flossers', 'picks',
  'oil', 'pulling', 'cocomint', 'coconut', 'peppermint', 'spearmint', 'mint', 'mints',
  'whitening', 'sensitivity', 'sensitive', 'hydroxyapatite', 'fluoride', 'neem',
  'turmeric', 'xylitol', 'charcoal', 'butter', 'gums', 'gum', 'tongue', 'scraper',
  'breath', 'spray', 'tablets', 'travel', 'kids', 'strips', 'essential', 'oils',
  'diffuser', 'lavender', 'eucalyptus', 'lemon', 'lemongrass', 'castor', 'vitamin',
  'seed', 'black', 'water', 'dental', 'oral', 'care', 'kit', 'probiotic', 'vegan'
];

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.querySelector('form.search').addEventListener('submit', this.onFormSubmit.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));
    this.querySelector('button[type="reset"]').addEventListener('click', this.clear.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    // A new user-typed term invalidates any typo-correction state
    this.typoTriedTerm = null;
    this.pendingCorrectionNotice = null;
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    // Apply the same digit->letter normalization to the full-page search
    // (Enter key / "Search for …" button) so a query like "16oz" resolves
    // there too, keeping it consistent with the predictive results.
    const normalized = this.normalizeSearchTerm(query);
    if (normalized !== query) this.input.value = normalized;
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
      this.close(true);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  // Shopify's predictive search tokenizes the query on whitespace and
  // prefix-matches each token, so a compound like "16oz" is one token that
  // never matches the catalog's "16 fl oz" — but "16 oz" (two tokens) does.
  // Insert a space at digit->letter boundaries so unit-style queries resolve
  // ("16oz" -> "16 oz", "5ml" -> "5 ml"). We deliberately do NOT split
  // letter->digit, which would break real product tokens like "D3", "K2", "B12".
  normalizeSearchTerm(term) {
    return term
      .replace(/(\d)([a-z])/gi, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getSearchResults(searchTerm) {
    const normalizedTerm = this.normalizeSearchTerm(searchTerm);
    const queryKey = normalizedTerm.replace(/\s+/g, "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(normalizedTerm)}&${encodeURIComponent('resources[type]')}=${encodeURIComponent('product,query,collection')}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.showCorrectionNotice();
    this.maybeCorrectTypo();
  }

  // ---- Typo fallback -------------------------------------------------------
  // When a term returns nothing, Shopify's predictive API gives no spelling
  // help (only the /search page does). So: take the word being typed, ask the
  // API for suggestions matching its first few letters, and fuzzy-match the
  // misspelled word against that vocabulary. If a close word exists, search
  // for it and label the results "Showing results for …".

  showCorrectionNotice() {
    const notice = this.pendingCorrectionNotice;
    if (!notice) return;
    this.pendingCorrectionNotice = null;

    const container = this.predictiveSearchResults.querySelector('.predictive-search-results:not(.is-empty)');
    if (!container) return;

    const note = document.createElement('p');
    note.className = 'predictive-search__correction';
    note.textContent = 'Showing results for ';
    const strong = document.createElement('strong');
    strong.textContent = `“${notice.to}”`;
    note.appendChild(strong);
    container.prepend(note);
  }

  async maybeCorrectTypo() {
    const term = this.getQuery();
    if (term !== this.searchTerm) return;
    if (this.typoTriedTerm === term) return;
    if (!this.predictiveSearchResults.querySelector('.predictive-search-results.is-empty')) return;

    const words = term.split(/\s+/);
    const target = words[words.length - 1];
    if (target.length < 4) return;

    this.typoTriedTerm = term;

    // The static vocabulary handles most real typos without any extra
    // API calls ("pasye" -> "paste", "muthwash" -> "mouthwash").
    if (this.applyCorrection(term, words, this.closestWord(target, PREDICTIVE_SEARCH_VOCABULARY))) return;

    // Otherwise build a vocabulary from the API with progressively shorter
    // prefixes of the typo: a typo can sit early in the word, so fall back
    // all the way to a single character before giving up.
    const prefixes = [...new Set([4, 3, 2, 1].map((n) => target.slice(0, n)))]
      .filter((prefix) => prefix.length >= 1 && prefix.length < target.length);

    for (const prefix of prefixes) {
      const candidates = await this.fetchTypoCandidates(prefix);
      if (this.getQuery() !== term) return; // user kept typing, correction is stale
      if (this.applyCorrection(term, words, this.closestWord(target, candidates))) return;
    }
  }

  applyCorrection(term, words, correctedWord) {
    if (!correctedWord) return false;
    const corrected = words.slice(0, -1).concat(correctedWord).join(' ');
    if (corrected.toLowerCase() === term.toLowerCase()) return false;
    this.pendingCorrectionNotice = { from: term, to: corrected };
    this.getSearchResults(corrected);
    return true;
  }

  fetchTypoCandidates(prefix) {
    return fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(prefix)}&${encodeURIComponent('resources[type]')}=${encodeURIComponent('product,query,collection')}&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text) return [];
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const texts = [];
        doc.querySelectorAll('.predictive-search__item-query-result').forEach((el) => {
          texts.push(el.getAttribute('aria-label') || el.textContent);
        });
        doc.querySelectorAll('.predictive-search__item-heading').forEach((el) => {
          texts.push(el.textContent);
        });

        const vocabulary = new Set();
        texts.join(' ').toLowerCase().split(/[^a-z0-9]+/).forEach((word) => {
          if (word.length >= 3) vocabulary.add(word);
        });
        return [...vocabulary];
      })
      .catch(() => []);
  }

  closestWord(target, candidates) {
    const typo = target.toLowerCase();
    const maxDistance = typo.length >= 5 ? 2 : 1;
    let best = null;
    let bestScore = maxDistance + 1;

    candidates.forEach((word) => {
      if (Math.abs(word.length - typo.length) > maxDistance) return;
      const distance = this.editDistance(typo, word);
      if (distance < bestScore) {
        bestScore = distance;
        best = word;
      } else if (distance === bestScore && best && word[0] === typo[0] && best[0] !== typo[0]) {
        best = word; // prefer candidates that start with the same letter
      }
    });

    return bestScore <= maxDistance ? best : null;
  }

  editDistance(a, b) {
    // Damerau-Levenshtein: insert/delete/substitute cost 1, and adjacent
    // transpositions ("pasye" -> "payse") also cost 1.
    const rows = a.length + 1;
    const cols = b.length + 1;
    const dist = [];
    for (let i = 0; i < rows; i++) {
      dist[i] = [i];
    }
    for (let j = 0; j < cols; j++) {
      dist[0][j] = j;
    }
    for (let i = 1; i < rows; i++) {
      for (let j = 1; j < cols; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dist[i][j] = Math.min(dist[i - 1][j] + 1, dist[i][j - 1] + 1, dist[i - 1][j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          dist[i][j] = Math.min(dist[i][j], dist[i - 2][j - 2] + 1);
        }
      }
    }
    return dist[a.length][b.length];
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    // The results popover opens directly under the search input, so measure
    // the space from there to the bottom of the viewport. The input (not the
    // form) is the anchor: in the header modal the results render inside the
    // form, so the form's bottom edge grows with them. Recomputed on every
    // open because scrolling moves the anchor.
    this.resultsMaxHeight = Math.round(window.innerHeight - this.input.getBoundingClientRect().bottom - 16);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    event.preventDefault();
    
    this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
